export const BRAND = {
  name: "BE HOT. NOW",
  lockup: "BeHot.Now",
  domain: "behot.now",
operator: "HRN Innovation Technologies Ltd",
  hrnUrl: "https://hrn.ae",
};

/** Public prices are displayed in USD. Charging, billing and invoicing happen in AED. */
export const CURRENCY = "AED";
export const PUBLIC_CURRENCY = "USD";

/** Fixed conversion used across BE HOT. NOW. */
export const AED_PER_USD = 4.33;

/** 1 Heat = US$1.00, charged as AED 4.33. All *_cents columns store AED cents. */
export const HEAT_AED_CENTS = 433;
export const HEAT_USD = 1;

/** Every bid is accepted: board entry starts at 5 Heat, then any whole Heat amount. */
export const BOOST_STEP_HEAT = 5;
export const BOOST_STEP_CENTS = BOOST_STEP_HEAT * HEAT_AED_CENTS;

/** Empty or freshly refreshed boards open at 3 Heat (US$3). */
export const EMPTY_BOARD_ENTRY_HEAT = 3;
export const EMPTY_BOARD_ENTRY_CENTS = EMPTY_BOARD_ENTRY_HEAT * HEAT_AED_CENTS;

/** One paid edit to a live listing: US$3.00. */
export const EDIT_FEE_CENTS = 1299;

/** Referral rewards, in AED cents (3 Heat on signup, 1 Heat per referred purchase). */
export const REFERRAL_SIGNUP_CENTS = 1299;
export const REFERRAL_PURCHASE_CENTS = 433;

/** Billing / invoice formatting — always AED. */
export function formatMoney(cents: number, currency = CURRENCY) {
  return new Intl.NumberFormat("en-AE", {
    style: "currency",
    currency,
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
  }).format(cents / 100);
}

/** Public / storefront formatting — USD, converted from stored AED cents. */
export function formatUsd(aedCents: number) {
  const usd = aedCents / 100 / AED_PER_USD;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: Number.isInteger(usd) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(usd);
}

/** Heat count from stored AED cents (1 Heat = AED 4.33 = US$1.00). */
export function formatHeat(cents: number) {
  return new Intl.NumberFormat("en-US").format(Math.round(cents / HEAT_AED_CENTS));
}

/** Heat -> AED cents. */
export function heatToCents(heat: number) {
  return Math.round(heat * HEAT_AED_CENTS);
}



export function slugify(input: string) {
  return input
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[^\w\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .slice(0, 60);
}

export function ensureHttps(url: string | null | undefined): string | null {
  if (!url) return null;
  const trimmed = url.trim();
  if (!trimmed) return null;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

export function canonicalDomain(url: string | null | undefined) {
  if (!url) return null;
  try {
    const withProtocol = url.startsWith("http") ? url : `https://${url}`;
    const host = new URL(withProtocol).hostname.toLowerCase();
    return host.replace(/^www\./, "");
  } catch {
    return null;
  }
}

/**
 * Append BeHot.Now attribution + UTM parameters to an outbound listing link.
 * Existing params on the target URL are preserved; existing utm_* are not overwritten.
 */
export function outboundUrl(
  url: string | null | undefined,
  opts: {
    slug?: string | null;
    medium?: string;
    content?: string | null;
    term?: string | null;
  } = {},
) {
  if (!url) return undefined;
  try {
    const target = new URL(url.startsWith("http") ? url : `https://${url}`);
    const params: Record<string, string> = {
      utm_source: "BeHot.Now",
      utm_medium: opts.medium ?? "referral",
      utm_campaign: opts.slug ? `behot-listing-${opts.slug}` : "behot-listing",
    };
    if (opts.content) params['utm_content'] = opts.content;
    if (opts.term) params['utm_term'] = opts.term;
    for (const [k, v] of Object.entries(params)) {
      if (!target.searchParams.has(k)) target.searchParams.set(k, v);
    }
    if (!target.searchParams.has("ref")) target.searchParams.set("ref", "behot.now");
    return target.toString();
  } catch {
    return url;
  }
}



export const LISTING_TYPES = [
  "company",
  "product",
  "startup",
  "app",
  "ai_product",
  "saas",
  "brand",
  "creator",
  "influencer",
  "ugc_creator",
  "artist",
  "dj",
  "producer",
  "musician",
  "band",
  "song",
  "label",
  "streamer",
  "podcaster",
  "model",
  "game",
  "venue",
  "event",
  "local_business",
  "web3_project",
  "depin_project",
  "other",
] as const;

export type ListingTypeValue = (typeof LISTING_TYPES)[number];

export function labelForType(value: string) {
  return value.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
